import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import Spinner from '../../../components/Spinner';
import CustomizedSwitch from '../../../components/Switch';
import FeatureFlagService, { FeatureFlagFormPayload, FeatureFlagTargeting } from '../../../services/feature-flag.service';
import ProductService from '../../../services/product.service';
import MasterService from '../../../services/master.service';
import { Modules, AuthRoutes } from '../../../enums';

interface ProductOption {
  id: number;
  product_code: string;
  product_name: string;
}

interface StatusOption {
  id: number;
  status_name: string;
}

interface FeatureFlagFormState {
  flag_code: string;
  flag_name: string;
  product_id: number | '';
  default_state: boolean;
  targeting: FeatureFlagTargeting;
  status_id: number | '';
  description: string;
  notes: string;
}

const initialForm: FeatureFlagFormState = {
  flag_code: '',
  flag_name: '',
  product_id: '',
  default_state: false,
  targeting: 'all_tenants',
  status_id: '',
  description: '',
  notes: '',
};

const TARGETING_OPTIONS: { value: FeatureFlagTargeting; label: string }[] = [
  { value: 'all_tenants', label: 'All Tenants' },
  { value: 'by_plan', label: 'By Plan' },
  { value: 'by_tenant', label: 'By Tenant' },
];

const FeatureFlagForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;

  const [formData, setFormData] = useState<FeatureFlagFormState>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [checkingCode, setCheckingCode] = useState(false);

  useEffect(() => {
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
    MasterService.list({ type: 'featureFlagStatuses', includeDeleted: false, limit: 100 }).then((response: any) => {
      setStatuses(response?.data?.items ?? []);
    });
  }, []);

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    FeatureFlagService.get(id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          flag_code: row.flag_code ?? '',
          flag_name: row.flag_name ?? '',
          product_id: row.product_id ?? '',
          default_state: row.default_state ?? false,
          targeting: row.targeting ?? 'all_tenants',
          status_id: row.status_id ?? '',
          description: row.description ?? '',
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id]);

  const handleFlagCodeBlur = useCallback(async () => {
    if (!formData.flag_code.trim() || !formData.product_id) return;
    setCheckingCode(true);
    try {
      const response: any = await FeatureFlagService.checkCode(
        formData.flag_code.trim(),
        Number(formData.product_id),
        edit && id ? Number(id) : undefined
      );
      if (!response?.data?.unique) {
        setErrors((prev) => ({ ...prev, flag_code: t('featureFlags.form.pageTexts.fields.flagCode.errors.duplicate') }));
      } else {
        setErrors((prev) => {
          const next = { ...prev };
          delete next.flag_code;
          return next;
        });
      }
    } finally {
      setCheckingCode(false);
    }
  }, [formData.flag_code, formData.product_id, edit, id, t]);

  const validate = useCallback(
    (data: FeatureFlagFormState) => {
      const nextErrors: Record<string, string> = {};
      if (!data.flag_code.trim()) nextErrors.flag_code = t('featureFlags.form.pageTexts.fields.flagCode.errors.required');
      if (!data.flag_name.trim()) nextErrors.flag_name = t('featureFlags.form.pageTexts.fields.flagName.errors.required');
      if (!data.product_id) nextErrors.product_id = t('featureFlags.form.pageTexts.fields.product.errors.required');
      if (!data.status_id) nextErrors.status_id = t('featureFlags.form.pageTexts.fields.status.errors.required');
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const numericFields = ['product_id', 'status_id'];
    const value = numericFields.includes(name) ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload: FeatureFlagFormPayload = {
          flag_code: formData.flag_code.trim(),
          flag_name: formData.flag_name.trim(),
          product_id: Number(formData.product_id),
          default_state: formData.default_state,
          targeting: formData.targeting,
          status_id: Number(formData.status_id),
          description: formData.description || undefined,
          notes: formData.notes || undefined,
        };

        if (edit && id) {
          await FeatureFlagService.update(id, payload);
          toast.success(<Toast message={t('featureFlags.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          await FeatureFlagService.create(payload);
          toast.success(<Toast message={t('featureFlags.form.pageTexts.addSuccess')} />, { icon: false });
        }
        navigate(`${Modules.home}${AuthRoutes.featureFlags}`);
      } finally {
        setLoading(false);
      }
    },
    [formData, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('featureFlags.form.pageTexts.editTitle') : t('featureFlags.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-4">
          <Input
            id="flag_code"
            name="flag_code"
            label={t('featureFlags.form.pageTexts.fields.flagCode.label')}
            placeholder={t('featureFlags.form.pageTexts.fields.flagCode.placeholder')}
            required
            value={formData.flag_code}
            error={!!errors.flag_code}
            errorMessage={checkingCode ? t('featureFlags.form.pageTexts.fields.flagCode.checking') : errors.flag_code}
            onChange={handleChange}
            onBlur={handleFlagCodeBlur}
          />
        </div>

        <div className="col-md-4">
          <Input
            id="flag_name"
            name="flag_name"
            label={t('featureFlags.form.pageTexts.fields.flagName.label')}
            placeholder={t('featureFlags.form.pageTexts.fields.flagName.placeholder')}
            required
            value={formData.flag_name}
            error={!!errors.flag_name}
            errorMessage={errors.flag_name}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="product_id">
            {t('featureFlags.form.pageTexts.fields.product.label')}
          </label>
          <select
            id="product_id"
            name="product_id"
            className={`form-select ${errors.product_id ? 'is-invalid' : ''}`}
            value={formData.product_id}
            disabled={edit}
            onChange={(e) => handleChange(e, 'product_id')}
          >
            <option value="">{t('featureFlags.form.pageTexts.fields.product.placeholder')}</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.product_code} — {product.product_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4 d-flex align-items-end">
          <CustomizedSwitch
            label={
              formData.default_state
                ? t('featureFlags.form.pageTexts.fields.defaultState.on')
                : t('featureFlags.form.pageTexts.fields.defaultState.off')
            }
            name="default_state"
            checked={formData.default_state}
            onChange={() => setFormData((prev) => ({ ...prev, default_state: !prev.default_state }))}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label d-block">{t('featureFlags.form.pageTexts.fields.targeting.label')}</label>
          <div className="d-flex gap-3">
            {TARGETING_OPTIONS.map((option) => (
              <div className="form-check" key={option.value}>
                <input
                  type="radio"
                  className="form-check-input"
                  id={`targeting_${option.value}`}
                  name="targeting"
                  checked={formData.targeting === option.value}
                  onChange={() => setFormData((prev) => ({ ...prev, targeting: option.value }))}
                />
                <label className="form-check-label" htmlFor={`targeting_${option.value}`}>
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="status_id">
            {t('featureFlags.form.pageTexts.fields.status.label')}
          </label>
          <select
            id="status_id"
            name="status_id"
            className={`form-select ${errors.status_id ? 'is-invalid' : ''}`}
            value={formData.status_id}
            onChange={(e) => handleChange(e, 'status_id')}
          >
            <option value="">{t('featureFlags.form.pageTexts.fields.status.placeholder')}</option>
            {statuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.status_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <Input
            id="description"
            name="description"
            label={t('featureFlags.form.pageTexts.fields.description.label')}
            value={formData.description}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-6">
          <Input
            id="notes"
            name="notes"
            label={t('featureFlags.form.pageTexts.fields.notes.label')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>

        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('featureFlags.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.home}${AuthRoutes.featureFlags}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('featureFlags.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default FeatureFlagForm;
